import {
  hasBundleChildStatuses,
  normalizeGridSyncStatus,
  resolveBundleStatus,
} from 'src/app/features/listing-product-table-lite/bundleStatusUtils'
import {BaseNames} from 'src/marketConnect/helpers'

export type ProductLinkStatus = 'linked' | 'unlinked'

const normalizeChannelType = (type?: string) => {
  const raw = (type || '').trim().toLowerCase().replace(/\s+/g, '_')
  if (raw === 'erp-secondary') return 'erp_secondary'
  return raw
}

const isLinkedStatus = (status?: string | null) => {
  const normalizedStatus = normalizeGridSyncStatus(status)
  return normalizedStatus === 'SYNCED' || normalizedStatus === 'LINKED'
}

const getBundleItems = (data: any) => {
  const items = data?.bundle?.items
  return Array.isArray(items) ? items : []
}

const getBundleItemStatus = (item: any, type: string) => {
  const statusKey = type === 'primary' ? 'primary_sync_status' : `${type}_sync_status`
  return item?.[statusKey] ?? item?.sync_status ?? item?.primary_sync_status ?? null
}

const hasBundleLinkedStatus = (data: any, type: string) => {
  const normalizedType = normalizeChannelType(type)
  const matchingItems = getBundleItems(data).filter((item: any) => {
    const itemType = normalizeChannelType(item?.type)
    return itemType === normalizedType
  })

  if (!matchingItems.length) {
    return false
  }

  return matchingItems.every((item: any) => {
    const linkedVariantId = item?.platform_product_variant ?? item?.id ?? item?.parent
    return Boolean(linkedVariantId && isLinkedStatus(getBundleItemStatus(item, normalizedType)))
  })
}

export const formatChannelLabel = (type: string) => {
  if (!type) {
    return ''
  }

  return type
    .split('_')
    .map((segment) => {
      const lower = segment.toLowerCase()
      if (lower === 'primary') {
        return BaseNames.PRIMARY_Name
      }
      if (lower === 'secondary') {
        return BaseNames.SECONDARY_Name
      }
      if (lower === 'erp') {
        return 'ERP'
      }
      if (lower === 'oms') {
        return 'OMS'
      }
      return segment.charAt(0).toUpperCase() + segment.slice(1)
    })
    .join(' ')
}

export const getProductChannelLinkedStatus = (
  data: any,
  type: string,
  platformId?: string | number
): ProductLinkStatus => {
  const normalizedType = normalizeChannelType(type)

  if (normalizedType === 'primary') {
    const status = hasBundleChildStatuses(data, 'sync_status')
      ? resolveBundleStatus(data, 'sync_status', data?.sync_status)
      : data?.sync_status
    return (data?.parent && isLinkedStatus(status)) || hasBundleLinkedStatus(data, normalizedType)
      ? 'linked'
      : 'unlinked'
  }

  const specificStatusField = platformId ? `${normalizedType}_sync_status_${platformId}` : null
  let status: string | null = null

  if (specificStatusField && data?.[specificStatusField] != null) {
    status = hasBundleChildStatuses(data, specificStatusField)
      ? resolveBundleStatus(data, specificStatusField, data?.[specificStatusField])
      : data?.[specificStatusField]
  }

  if (!status) {
    const genericStatusField = `${normalizedType}_sync_status`
    status = hasBundleChildStatuses(data, genericStatusField)
      ? resolveBundleStatus(data, genericStatusField, data?.[genericStatusField])
      : data?.[genericStatusField]
  }

  return (data?.[`${normalizedType}_parent`] && isLinkedStatus(status)) ||
    hasBundleLinkedStatus(data, normalizedType)
    ? 'linked'
    : 'unlinked'
}

const SOURCE_STORE_LINK_ACTION_TYPES = ['accounting', 'shipping', 'erp', 'erp_secondary']

/** Matches Link Actions VIEW/LINK NEW visibility with Linking Status column rules. */
export const isProductLinkedForLinkActions = (
  data: any,
  channels: any[] = [],
  options: { mainStore?: boolean; erpStore?: boolean } = {}
): boolean => {
  const { mainStore = false, erpStore = false } = options
  const isPrimaryContext = mainStore || erpStore

  if (isPrimaryContext) {
    return SOURCE_STORE_LINK_ACTION_TYPES.some(
      (type) => getProductChannelLinkedStatus(data, type) === 'linked'
    )
  }

  if (!Array.isArray(channels) || channels.length === 0) {
    return false
  }

  return channels.some((item) => getProductChannelLinkedStatus(data, item?.type, item?.id) === 'linked')
}

export const getChannelDisplayName = (item: any): string => {
  const channelLabel = formatChannelLabel(item?.type)
  const baseLabel = channelLabel ? `${channelLabel} channel` : 'Channel'
  const platformDisplayName = item?.display_name || item?.platform_info?.display_name || item?.name

  if (!platformDisplayName) {
    return baseLabel
  }

  return `${baseLabel} - ${platformDisplayName}`
}

export const formatLinkingStatusForCSV = (data: any, channels: any[] = []) => {
  if (!Array.isArray(channels) || channels.length === 0) {
    return ''
  }

  return channels
    .map((item) => {
      const isLinked = getProductChannelLinkedStatus(data, item?.type, item?.id) === 'linked'
      return `${isLinked ? '\u2713' : 'X'} ${getChannelDisplayName(item)}`
    })
    .join('\n')
}
